import React, { useState, useCallback } from 'react';
import {
  Collapse,
  Tag,
  Button,
  Tooltip,
  InputNumber,
  Typography,
  Space,
  Badge,
  Divider,
  Alert,
} from 'antd';
import {
  RobotOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  ToolOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { EstimateLineItem, ResearchStep, ResearchPart } from '../types';
import { proceduresApi } from '../services/api';

const { Text } = Typography;
const { Panel } = Collapse;

// ─── Helper: author badge ───────────────────────────────────────────────────

function AuthorBadge({
  role,
  reviewedAt,
}: {
  role?: string | null;
  reviewedAt?: string | null;
}) {
  if (reviewedAt) {
    return (
      <Tooltip title={`Reviewed ${new Date(reviewedAt).toLocaleDateString()}`}>
        <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: 11 }}>
          Verified
        </Tag>
      </Tooltip>
    );
  }
  if (!role || role === 'ai') {
    return (
      <Tooltip title="Generated by AI — not yet reviewed by shop staff">
        <Tag icon={<RobotOutlined />} color="default" style={{ fontSize: 11 }}>
          AI
        </Tag>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={`Authored by ${role}`}>
      <Tag icon={<EditOutlined />} color="blue" style={{ fontSize: 11 }}>
        {role}
      </Tag>
    </Tooltip>
  );
}

// ─── Helper: confidence bar ─────────────────────────────────────────────────

function ConfidenceBar({ value }: { value?: number | null }) {
  if (value == null) return null;
  const pct = Math.round(value * 100);
  const color = pct >= 75 ? '#52c41a' : pct >= 50 ? '#faad14' : '#ff4d4f';
  return (
    <Tooltip title={`AI synthesis confidence: ${pct}%`}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <span
          style={{
            display: 'inline-block',
            width: 60,
            height: 6,
            background: '#f0f0f0',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <span
            style={{
              display: 'block',
              width: `${pct}%`,
              height: '100%',
              background: color,
              borderRadius: 3,
              transition: 'width 0.3s',
            }}
          />
        </span>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {pct}%
        </Text>
      </span>
    </Tooltip>
  );
}

// ─── Props ──────────────────────────────────────────────────────────────────

interface Props {
  item: EstimateLineItem;
  itemIndex: number;
  /** Called when user edits a step's hours — parent updates labor_hours total */
  onLaborHoursChange?: (index: number, newTotal: number) => void;
  /** Called when procedure is marked reviewed via API */
  onReviewed?: (index: number) => void;
  /** Read-only mode (e.g. on declined items) */
  readOnly?: boolean;
}

// ─── Component ──────────────────────────────────────────────────────────────

const LineItemProcedureEditor: React.FC<Props> = ({
  item,
  itemIndex,
  onLaborHoursChange,
  onReviewed,
  readOnly = false,
}) => {
  const steps: ResearchStep[] = item.procedure_steps ?? [];
  const parts: ResearchPart[] = item.parts_researched ?? [];
  const tools: string[] = item.special_tools_researched ?? [];



  // Per-step editable hours state
  const [editedHours, setEditedHours] = useState<Record<number, number>>(() =>
    Object.fromEntries(steps.map((s, i) => [i, s.hours])),
  );
  const [reviewLoading, setReviewLoading] = useState(false);

  const totalHours = steps.length > 0
    ? Object.values(editedHours).reduce((a, b) => a + b, 0)
    : item.labor_hours ?? 0;

  const originalTotal = steps.reduce((a, s) => a + s.hours, 0);
  const isEdited = Math.abs(totalHours - originalTotal) > 0.01;

  const handleStepHoursChange = useCallback(
    (stepIdx: number, val: number | null) => {
      if (val == null) return;
      setEditedHours(prev => {
        const next = { ...prev, [stepIdx]: val };
        const newTotal = Object.values(next).reduce((a, b) => a + b, 0);
        onLaborHoursChange?.(itemIndex, Math.round(newTotal * 100) / 100);
        return next;
      });
    },
    [itemIndex, onLaborHoursChange],
  );

  const handleResetSteps = useCallback(() => {
    const reset = Object.fromEntries(steps.map((s, i) => [i, s.hours]));
    setEditedHours(reset);
    onLaborHoursChange?.(itemIndex, originalTotal);
  }, [steps, originalTotal, itemIndex, onLaborHoursChange]);

  const handleMarkReviewed = useCallback(async () => {
    if (!item.procedure_id) return;
    setReviewLoading(true);
    try {
      await proceduresApi.review(item.procedure_id);
      onReviewed?.(itemIndex);
    } catch (err) {
      console.error('Failed to mark procedure as reviewed', err);
    } finally {
      setReviewLoading(false);
    }
  }, [item.procedure_id, itemIndex, onReviewed]);

  // No procedure data at all — render nothing
  if (steps.length === 0 && parts.length === 0 && tools.length === 0) return null;

  // ── Header ────────────────────────────────────────────────────────────────

  const headerContent = (
    <Space size={8} wrap>
      <Text strong style={{ fontSize: 12 }}>
        {steps.length > 0 ? `📋 ${steps.length} steps` : '📋 Procedure'}
        {steps.length > 0 && (
          <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
            · {Math.round(totalHours * 10) / 10}h
            {isEdited && <Text type="warning" style={{ marginLeft: 3 }}>(edited)</Text>}
          </Text>
        )}
      </Text>

      <AuthorBadge
        role={item.procedure_author_role}
        reviewedAt={item.procedure_reviewed_at}
      />

      {item.synthesis_confidence != null && (
        <ConfidenceBar value={item.synthesis_confidence} />
      )}

      {item.procedure_year_range_warning && (
        <Tooltip title="Matched from a different model year range — verify steps apply to this vehicle">
          <Tag icon={<WarningOutlined />} color="warning" style={{ fontSize: 11 }}>
            Year range match
          </Tag>
        </Tooltip>
      )}
    </Space>
  );

  return (
    <div style={{ marginTop: 8 }}>
      <Collapse
        size="small"
        ghost
        style={{ border: '1px solid #f0f0f0', borderRadius: 6 }}
      >
        <Panel header={headerContent} key="procedure">
          {/* ── Steps ─────────────────────────────────────────────────── */}
          {steps.length > 0 && (
            <>
              <div style={{ marginBottom: 8 }}>
                {steps.map((step, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                      padding: '4px 0',
                      borderBottom: idx < steps.length - 1 ? '1px solid #fafafa' : 'none',
                    }}
                  >
                    <Text
                      type="secondary"
                      style={{ fontSize: 11, minWidth: 20, paddingTop: 2 }}
                    >
                      {idx + 1}.
                    </Text>
                    <Text style={{ flex: 1, fontSize: 12 }}>{step.step}</Text>
                    {readOnly ? (
                      <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                        {step.hours}h
                      </Text>
                    ) : (
                      <InputNumber
                        size="small"
                        min={0.1}
                        max={40}
                        step={0.1}
                        precision={1}
                        value={editedHours[idx] ?? step.hours}
                        onChange={val => handleStepHoursChange(idx, val)}
                        suffix="h"
                        style={{ width: 72 }}
                      />
                    )}
                  </div>
                ))}
              </div>

              {!readOnly && (
                <Space style={{ marginBottom: 8 }}>
                  <Text strong style={{ fontSize: 12 }}>
                    Total: {Math.round(totalHours * 10) / 10}h
                  </Text>
                  {isEdited && (
                    <Button
                      type="link"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={handleResetSteps}
                      style={{ fontSize: 11 }}
                    >
                      Reset to AI estimate
                    </Button>
                  )}
                </Space>
              )}
            </>
          )}

          {/* ── Special Tools ──────────────────────────────────────────── */}
          {tools.length > 0 && (
            <>
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ marginBottom: 6 }}>
                <Text
                  type="secondary"
                  style={{ fontSize: 11, display: 'block', marginBottom: 4 }}
                >
                  <ToolOutlined style={{ marginRight: 4 }} />
                  Special tools required:
                </Text>
                <Space wrap size={4}>
                  {tools.map((tool, i) => (
                    <Tag key={i} style={{ fontSize: 11 }}>
                      {tool}
                    </Tag>
                  ))}
                </Space>
              </div>
            </>
          )}

          {/* ── Parts ─────────────────────────────────────────────────── */}
          {parts.length > 0 && (
            <>
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ marginBottom: 6 }}>
                <Text
                  type="secondary"
                  style={{ fontSize: 11, display: 'block', marginBottom: 4 }}
                >
                  <ThunderboltOutlined style={{ marginRight: 4 }} />
                  AI-researched parts ({parts.length}):
                </Text>
                <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ color: '#8c8c8c' }}>
                      <th style={{ textAlign: 'left', paddingBottom: 4 }}>Description</th>
                      <th style={{ textAlign: 'left', paddingBottom: 4 }}>Part #</th>
                      <th style={{ textAlign: 'center', paddingBottom: 4 }}>Qty</th>
                      <th style={{ textAlign: 'center', paddingBottom: 4 }}>OEM</th>
                      <th style={{ textAlign: 'left', paddingBottom: 4 }}>Fits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parts.map((part, i) => (
                      <tr key={i}>
                        <td style={{ paddingBottom: 2 }}>{part.description}</td>
                        <td style={{ paddingBottom: 2, fontFamily: 'monospace' }}>
                          {part.part_number ?? '—'}
                        </td>
                        <td style={{ paddingBottom: 2, textAlign: 'center' }}>{part.qty}</td>
                        <td style={{ paddingBottom: 2, textAlign: 'center' }}>
                          {part.oem ? '✓' : ''}
                        </td>
                        <td style={{ paddingBottom: 2, color: '#8c8c8c' }}>
                          {part.fit_year_from && part.fit_year_to
                            ? `${part.fit_year_from}–${part.fit_year_to}`
                            : part.fit_year_from
                              ? `${part.fit_year_from}+`
                              : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ── Sources ───────────────────────────────────────────────── */}
          {(item.procedure_sources?.length ?? 0) > 0 && (
            <>
              <Divider style={{ margin: '8px 0' }} />
              <Text type="secondary" style={{ fontSize: 11 }}>
                <InfoCircleOutlined style={{ marginRight: 4 }} />
                Sources:{' '}
                {item.procedure_sources!.slice(0, 3).map((url, i) => (
                  <span key={i}>
                    {i > 0 && ', '}
                    <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11 }}>
                      [{i + 1}]
                    </a>
                  </span>
                ))}
              </Text>
            </>
          )}

          {/* ── Review action ─────────────────────────────────────────── */}
          {!readOnly && item.procedure_id && !item.procedure_reviewed_at && (
            <>
              <Divider style={{ margin: '8px 0' }} />
              <Button
                type="dashed"
                size="small"
                icon={<CheckCircleOutlined />}
                loading={reviewLoading}
                onClick={handleMarkReviewed}
                style={{ fontSize: 11 }}
              >
                Mark as Reviewed
              </Button>
            </>
          )}
        </Panel>
      </Collapse>
    </div>
  );
};

export default LineItemProcedureEditor;
